import { useMemo } from "react";
import {
  Avatar,
  AvatarGroup,
  SkeletonBlock,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import {
  useFolderSharing,
  HydratedFolderShare,
  useCurrentFolder,
} from "@zapier/client";

import { getInitials } from "@zapier/shared";

import style from "./FolderHeaderSharing.module.css";

const FolderHeaderSharing = () => {
  const t = useTranslations("folder-header-sharing");
  const folder = useCurrentFolder();
  const { shares, isSharesLoading } = useFolderSharing({
    folderId: folder?.id,
    rootId: folder?.rootId,
  });

  const MAX_AVATARS = 4;

  const overflowCopy = useMemo(() => {
    const remainder = shares?.length - MAX_AVATARS;
    if (Number.isNaN(remainder)) {
      return "";
    }
    return `${remainder}`;
  }, [shares.length]);

  // In the folder view, don't show the owner again in the UI as a folder share, as it's redundant
  const filteredShares = useMemo(() => {
    return (
      shares &&
      Array.isArray(shares) &&
      shares.filter(
        (share: any) => (share?.member?.id || share.id) !== folder?.ownerId,
      )
    );
  }, [shares, folder?.ownerId]);

  if (!folder) {
    return;
  }

  const ownerName = folder?.owner?.fullName || "";
  const ownerLabel = ownerName ? t("tooltips.owner", { ownerName }) : "";
  return (
    <div className={style.folderHeaderSharing}>
      {isSharesLoading ? (
        <SkeletonBlock borderRadius="large" height="36px" width="100px" />
      ) : (
        <AvatarGroup
          size="medium"
          max={MAX_AVATARS}
          overflowCopy={overflowCopy}
        >
          <Tooltip allowMultiline={true} content={ownerLabel} placement="left">
            <TooltipFocusableTrigger>
              <Avatar
                isBlock={true}
                name={ownerName}
                size="medium"
                url={folder?.owner?.photoUrl}
              />
            </TooltipFocusableTrigger>
          </Tooltip>
          {filteredShares &&
            filteredShares.map((share: HydratedFolderShare, _index) => {
              const url =
                share?.member?.photoUrl ||
                share?.team?.logo ||
                share?.account?.logoUrl ||
                "";

              const accountName = share?.account?.name || "";
              const adminsAtAccountName = accountName
                ? t("labels.admins-at", { accountName })
                : "";
              const sharedWithName =
                share?.shareType === "accountadmin" && adminsAtAccountName
                  ? adminsAtAccountName
                  : share?.member?.fullName ||
                    share?.team?.name ||
                    accountName ||
                    "";

              if (!sharedWithName) {
                return;
              }

              const tooltipLabel = t("tooltips.shared-with", {
                sharedWithName,
              });

              return (
                <Tooltip
                  allowMultiline={true}
                  content={tooltipLabel}
                  placement="left"
                  key={share.id}
                >
                  <TooltipFocusableTrigger>
                    <Avatar
                      initials={getInitials(sharedWithName)}
                      isBlock={true}
                      name={tooltipLabel}
                      size="medium"
                      url={url}
                    />
                  </TooltipFocusableTrigger>
                </Tooltip>
              );
            })}
        </AvatarGroup>
      )}
    </div>
  );
};

export default FolderHeaderSharing;
